import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import Avatar from '../../../Avatar.jsx';
import Badge from '../../../Badge.jsx';
import { toneFor } from '../constants/statTones';
import { slotCells } from '../shared';

/** Initials from a display name, for the avatar fallback. */
const initialsOf = (name) =>
  name
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

/**
 * The key fields of one record.
 *
 * ## Reading the contract (CT-5641)
 *
 * `PROFILE_CARD` publishes one record's `slots`, the same six buckets `LIST`
 * uses (see `recordSlots.js`), plus `row_key` and `avatar_url`. The card is
 * four bands, and which band a field lands in is the author's binding, not a
 * decision taken here — hard-coding Owner/Stage/Value would make this a deals
 * widget rather than a profile card.
 *
 * 1. **Identity.** The avatar, the first `identity` cell as the name, the rest
 *    beneath it *with their labels* — `NIT 900.123.456-7` says what the number
 *    is, where a bare second line does not. `status` rides alongside as a
 *    badge: the state of a record belongs with its name.
 *
 * 2. **Signals.** Their own band, in their own tone. `signal` is the slot
 *    whose entire purpose is "look at this" — an overdue balance, a breach —
 *    and it used to render as one more badge beside the name, which is where
 *    things go to be ignored. A band crossing the card cannot be skimmed past.
 *
 * 3. **Facts.** Two columns, per `display.fact_columns`. Four facts in one
 *    column spend twice the height in a card that usually sits in a short
 *    slot, and facts are read by label — a grid is the natural shape.
 *
 * 4. **Context and source.** Grouped at the foot, muted. They say where the
 *    record sits and where it came from; mixed into the facts they read as
 *    attributes of the record itself.
 */
function ProfileCardWidget({ data, className = '' }) {
  const card = useMemo(() => {
    if (data?.slots) {
      const identity = slotCells(data.slots, 'identity');
      return {
        name: identity[0]?.text ?? '',
        /** Keeps the label: a second identity line is an identifier, not a tagline. */
        identifiers: identity.slice(1),
        avatarUrl: data.avatar_url ?? null,
        badges: slotCells(data.slots, 'status'),
        signals: slotCells(data.slots, 'signal'),
        facts: slotCells(data.slots, 'fact'),
        footnotes: [...slotCells(data.slots, 'context'), ...slotCells(data.slots, 'source')],
      };
    }
    // The pre-v2 shape, so a caller that still passes `fields` keeps working.
    return {
      name: data?.name ?? '',
      identifiers: data?.subtitle ? [{ key: 'subtitle', label: '', text: data.subtitle }] : [],
      avatarUrl: data?.avatarUrl ?? null,
      badges: [],
      signals: [],
      facts: (data?.fields ?? []).map((field) => ({
        key: field.label, label: field.label, text: field.value,
      })),
      footnotes: [],
    };
  }, [data]);

  if (!card.name) return null;

  return (
    <div className={`flex flex-col gap-2.5 ${className}`.trim()}>
      <div className="flex items-start gap-2.5">
        <Avatar size="md" src={card.avatarUrl} alt={card.name} initials={initialsOf(card.name)} />
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="truncate text-[13px] font-semibold text-content">{card.name}</span>
            {card.badges.map((badge) => (
              <Badge key={badge.key} size="small" variant={toneFor(badge.tone).badge}>
                {badge.text}
              </Badge>
            ))}
          </div>
          {card.identifiers.map((cell) => (
            <div key={cell.key} className="mt-px flex min-w-0 gap-1 text-[11px]">
              {cell.label ? <span className="shrink-0 text-content-faint">{cell.label}</span> : null}
              <span className="truncate text-content-muted">{cell.text}</span>
            </div>
          ))}
        </div>
      </div>

      {card.signals.length ? (
        <div className="flex flex-col gap-1">
          {card.signals.map((signal) => (
            <div
              key={signal.key}
              className="flex items-baseline justify-between gap-2 rounded-md border-l-2 border-red-500 bg-red-500/8 px-2 py-1.5 dark:border-red-400 dark:bg-red-400/10"
            >
              <span className="min-w-0 truncate text-[11px] font-medium text-content">
                {signal.label}
              </span>
              <span className="shrink-0 text-xs font-semibold text-red-600 dark:text-red-400">
                {signal.text}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {card.facts.length ? (
        <dl className="m-0 grid grid-cols-2 gap-x-3 gap-y-1.5">
          {card.facts.map((fact) => (
            <div key={fact.key} className="flex min-w-0 flex-col">
              <dt className="truncate text-[10px] text-content-faint">{fact.label}</dt>
              <dd className="m-0 truncate text-xs font-medium text-content">{fact.text}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {card.footnotes.length ? (
        <div className="flex flex-col gap-0.5 border-t border-edge pt-1.5">
          {card.footnotes.map((cell) => (
            <span key={cell.key} className="flex min-w-0 gap-1 text-[10px]">
              <span className="shrink-0 text-content-faint">{cell.label}</span>
              <span className="truncate text-content-muted">{cell.text}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

ProfileCardWidget.propTypes = {
  /** `PROFILE_CARD`'s `{row_key, avatar_url, slots}`. The pre-v2 flat shape still renders. */
  data: PropTypes.shape({
    row_key: PropTypes.string,
    avatar_url: PropTypes.string,
    slots: PropTypes.object,
    // pre-v2
    name: PropTypes.string,
    subtitle: PropTypes.string,
    avatarUrl: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  className: PropTypes.string,
};

export default memo(ProfileCardWidget);
